"use client";

import { RootState, useAppDispatch, useAppSelector } from "@/lib/redux/root";
import {
	getLocationRequested,
	updateUploadUrl,
} from "@/lib/redux/slices/location.slice";
import { Mail, Phone } from "lucide-react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useEffect } from "react";
import { useBrand } from "@/lib/hooks/useBrand";

const DEFAULT_CONTACT_NUMBER = "(833) 272-8880";
const DEFAULT_CONTACT_EMAIL = "info@e-arc.com";

export function Header() {
	const {
		loading,

		location: {
			locationName,
			contactEmail,
			contactNumber,
			appInfo,
			locationAddressId,
		},
	} = useAppSelector((state: RootState) => state.location);

	const hasAccountAddress = !!locationAddressId;
	const displayContactNumber = hasAccountAddress
		? contactNumber
		: DEFAULT_CONTACT_NUMBER;
	const displayContactEmail = hasAccountAddress
		? contactEmail
		: DEFAULT_CONTACT_EMAIL;

	const params = useParams();

	const searchParams = useSearchParams();
	const { brandConfig } = useBrand();

	const product = params?.product as string;
	const slug = params?.slug as string;

	const dispatch = useAppDispatch();

	const getQueryParams = () => {
		const p = new URLSearchParams(window.location.search);
		return {
			key: p.get("key") ?? "",
			rkey: p.get("rkey") ?? "",
			tappid: p.get("tappid") ?? "",
			applicationType: product === "arc" ? 1 : 2,
		};
	};

	// Build home URL: /{product}/{slug}?queryString (no extra routes)
	const getHomeUrl = () => {
		const queryString = searchParams.toString();
		const basePath = `/${slug}`;
		return queryString ? `${basePath}?${queryString}` : basePath;
	};

	useEffect(() => {
		if (!slug) return;
		const queryParams = getQueryParams();

		if (queryParams?.rkey) {
			dispatch(getLocationRequested(queryParams));
		} else {
			dispatch(getLocationRequested({ slug }));
		}
		dispatch(updateUploadUrl(window.location.href));
	}, [slug]);

	return (
		<>
			<header className='sticky top-0 z-50 w-full bg-white sm:static'>
				<div className='max-w-10xl mx-auto flex w-full flex-col gap-2 px-4 py-2 shadow-md sm:flex-row sm:items-center sm:justify-between sm:gap-0 sm:px-6'>
					{/* Row 1: Logo (left) | Upload target (right) */}
					<div className='flex w-full items-center justify-between sm:w-auto sm:justify-start sm:gap-4'>
						{loading ? (
							<div className='h-9 w-20 animate-pulse rounded bg-gray-300 sm:h-12 sm:w-36' />
						) : (
							<Link to='/'>
								<img
									src={
										appInfo?.logo ||
										import.meta.env.VITE_CDN_URL + brandConfig.logo
									}
									alt={`${appInfo?.name || brandConfig.name} Logo`}
									className='h-9 w-20 object-contain sm:h-12 sm:w-36 md:h-[60px] md:w-[180px]'
								/>
							</Link>
						)}

						<div className='flex flex-col items-end sm:items-start'>
							<span className='text-xs text-gray-600 sm:text-base'>
								{slug ? "Upload files to" : ""}
							</span>
							{loading ? (
								<span className='h-4 w-32 animate-pulse rounded bg-gray-300 sm:h-5 sm:w-40' />
							) : (
								hasAccountAddress && (
									<span
										className='text-sm font-semibold sm:text-xl'
										style={{ color: appInfo?.textColor || brandConfig.textColor }}
									>
										{locationName}
									</span>
								)
							)}
						</div>
					</div>

					{/* Row 2: Phone (left) | Email (right) */}
					<div className='flex w-full items-center justify-between sm:w-auto sm:justify-end sm:gap-6'>
						{displayContactNumber && displayContactNumber.length > 0 && (
							<a
								href={`tel:${displayContactNumber.replace(/\s+/g, "")}`}
								className='flex items-center gap-1.5 whitespace-nowrap text-gray-700 transition-colors sm:gap-2'
								style={
									{
										"--hover-color":
											appInfo?.hoverColor || brandConfig.hoverColor,
									} as React.CSSProperties
								}
								onMouseEnter={(e) =>
									(e.currentTarget.style.color =
										appInfo?.hoverColor || brandConfig.hoverColor)
								}
								onMouseLeave={(e) => (e.currentTarget.style.color = "")}
							>
								<Phone className='h-4 w-4 sm:h-5 sm:w-5' />
								<span
									className='text-sm font-medium sm:text-base'
									style={{ color: appInfo?.textColor || brandConfig.textColor }}
								>
									{displayContactNumber}
								</span>
							</a>
						)}

						{displayContactEmail && displayContactEmail.length > 0 && (
							<a
								href={`mailto:${displayContactEmail}`}
								target='_blank'
								rel='noopener noreferrer'
								className='flex items-center gap-1.5 whitespace-nowrap text-gray-700 transition-colors sm:gap-2'
								onMouseEnter={(e) =>
									(e.currentTarget.style.color =
										appInfo?.hoverColor || brandConfig.hoverColor)
								}
								onMouseLeave={(e) => (e.currentTarget.style.color = "")}
							>
								<Mail className='h-4 w-4 sm:h-5 sm:w-5' />
								<span
									className='text-sm font-medium sm:text-base'
									style={{ color: appInfo?.textColor || brandConfig.textColor }}
								>
									{displayContactEmail}
								</span>
							</a>
						)}
					</div>
				</div>
			</header>
		</>
	);
}
